import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ButtonModule, CheckboxModule, InputModule, RadioModule, SelectModule, TextareaModule } from '@sag-ui';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProductDamageDomesticForm, ProductDamageDomesticFormGroup } from '../../../models/cod-create.model';
import { AttachmentsInputComponent } from '../../../../shared/components/attachments-input/attachments-input.component';
import { filter, tap } from 'rxjs';
import { DamageIssueType, ItemCondition } from '@cod-monorepo/interfaces';
import { PackageType } from '@cod-monorepo/interfaces';
import { PackageTypeCondition } from '@cod-monorepo/interfaces';
import { Store } from '@ngxs/store';
import { AddCurrentCodLine, AddErrorMessage, RemoveErrorMessage } from '../../../state/cod.action';
import { ItemConditionMapping } from '../../../../shared/mappings/item-condition.mapping';
import { WorkflowError } from '../../../../shared/enums/workflow-error';
import { ManagerSelectComponent } from '../../../../shared/components/manager-select/manager-select.component';

@Component({
    selector: 'app-cod-product-damage-domestic-step',
    templateUrl: './product-damage-domestic.component.html',
    imports: [
        CommonModule,
        SelectModule,
        InputModule,
        FormsModule,
        ReactiveFormsModule,
        RadioModule,
        CheckboxModule,
        ButtonModule,
        TextareaModule,
        AttachmentsInputComponent,
        ManagerSelectComponent
    ],
    standalone: true
})
export class CodProductDamageDomesticStepComponent implements OnInit {
    @Input() productDamageDomesticForm: FormGroup<ProductDamageDomesticFormGroup> | undefined;

    private relatedCallTrackingNumberTouched = false;
    public damageTypes = DamageIssueType;
    public packageTypes = PackageType;
    public packageTypeConditions = PackageTypeCondition;
    public itemConditions = ItemCondition;
    public itemConditionMapping = ItemConditionMapping;

    constructor(private store: Store) { }

    ngOnInit(): void {
        this.initFinancialAdjustmentChangesListener();
        this.initCallTagTrackingNumberTouchedListener();
        this.initRelatedDocumentsListener();
    }

    public isDefined(control: keyof ProductDamageDomesticForm): boolean {
        return this.productDamageDomesticForm?.controls[control].value !== null;
    }

    public isDefinedItemCondition(): boolean {
        return (this.productDamageDomesticForm?.controls?.itemCondition?.value ?? []).length > 0;
    }

    public isTrue(control: keyof ProductDamageDomesticForm): boolean {
        return this.productDamageDomesticForm?.controls[control].value === true;
    }

    public isFalse(control: keyof ProductDamageDomesticForm): boolean {
        return this.productDamageDomesticForm?.controls[control].value === false;
    }

    public handleConfirm() {
        if (this.productDamageDomesticForm?.valid) {
            this.store.dispatch(new AddCurrentCodLine());
        }
    }

    public isValid(): boolean {
        return this.productDamageDomesticForm?.valid ?? false;
    }

    public itemConditionSelected(condition: ItemCondition): boolean {
        return this.productDamageDomesticForm?.controls.itemCondition.value?.includes(condition) ?? false;
    }

    public toggleItemCondition(condition: ItemCondition): void {
        const itemCondition = this.productDamageDomesticForm?.controls.itemCondition;

        if (itemCondition) {
            const conditions = itemCondition.value ?? [];

            if (conditions.includes(condition)) {
                itemCondition.setValue(conditions.filter(c => c !== condition));
            } else {
                itemCondition.setValue([...conditions, condition]);
            }
        }
    }

    private initFinancialAdjustmentChangesListener(): void {
        this.productDamageDomesticForm?.controls.financialAdjustment.valueChanges
            .pipe(
                filter(value => value === false),
                tap(() => {
                    this.productDamageDomesticForm?.controls.adjustmentAmount.setValue(0);
                    this.productDamageDomesticForm?.controls.filedWithin30Days.setValue(null);
                    this.productDamageDomesticForm?.controls.managerApprovedRequest.setValue(null);
                }))
            .subscribe();
    }

    private initCallTagTrackingNumberTouchedListener(): void {
        this.productDamageDomesticForm?.controls.callTagTrackingNumber.valueChanges
            .pipe(
                filter(() => !this.relatedCallTrackingNumberTouched),
                tap(() => {
                    this.store.dispatch(new AddErrorMessage(WorkflowError.UPLOAD_PROOF));

                    this.relatedCallTrackingNumberTouched = true;
                })
            )
            .subscribe();
    }

    private initRelatedDocumentsListener(): void {
        this.productDamageDomesticForm?.controls.relatedDocuments.valueChanges
            .pipe(
                filter(value => !!value && value.length > 0),
                tap(() => this.store.dispatch(new RemoveErrorMessage(WorkflowError.UPLOAD_PROOF)))
            )
            .subscribe();
    }
}
